// Las ventas del vendedor en una semana, con lo que pagó cada una. Sin datos del cliente.
// Mockup: surekeep/mockups/backoffice/portal-admin-vl-com-pagadas.html
import { CommissionAccrualStatusEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionAccrualStatusEnum';
import { WizardLineRoleEnum } from '@fiado/type-kit/bin/retailWizard/enums/WizardLineRoleEnum';
import { Button, Modal, Skeleton } from '@/kit';
import { formatDateTimeMx } from '@/lib/dateHelpers';
import { formatMoneyCents } from '@/shared/lib/format/money';
import { StatusPill } from '@/shared/patterns/Pills';
import { isSellerNotInPayout, useLiveSeller } from '../../api/commissionQueries';
import type { CommissionDirectory } from '../../api/useCommissionDirectory';

interface SellerSalesModalProps {
    sellerId: string;
    sellerName: string;
    /** `undefined` es el corte en curso. */
    periodId: string | undefined;
    weekTitle: string;
    directory: CommissionDirectory;
    onClose: () => void;
}

export function SellerSalesModal({
    sellerId,
    sellerName,
    periodId,
    weekTitle,
    directory,
    onClose,
}: SellerSalesModalProps) {
    const query = useLiveSeller(sellerId, periodId);
    const sales = query.data?.sales ?? [];
    const productName = (sku: string) => {
        const product = directory.product(sku);
        const name = product ? `${product.brand ?? ''} ${product.model ?? ''}`.trim() : '';
        return name === '' ? sku : name;
    };

    return (
        <Modal isOpen onClose={onClose} title={`Ventas de ${sellerName} · ${weekTitle}`} size="lg">
            {query.isLoading ? (
                <Skeleton lines={5} />
            ) : query.error && !isSellerNotInPayout(query.error) ? (
                <div className="flex items-center justify-between gap-2">
                    <p className="text-[12px] text-destructive">
                        No se pudieron leer las ventas de esa semana.
                    </p>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                            void query.refetch();
                        }}
                    >
                        Reintentar
                    </Button>
                </div>
            ) : sales.length === 0 ? (
                <p className="text-[12px] text-muted-foreground">
                    Sin ventas registradas esa semana
                </p>
            ) : (
                <ul className="flex flex-col divide-y divide-border">
                    {sales.map((sale) => {
                        const rules = [...new Set(sale.appliedBonuses.map((bonus) => bonus.name))];
                        const totalCents = sale.lines.reduce((sum, line) => sum + line.netCents, 0);
                        const products = sale.lines
                            .map(
                                (line) =>
                                    `${String(line.qty)}× ${productName(line.sku)}${line.role === WizardLineRoleEnum.GIFT ? ' (regalo)' : ''}`,
                            )
                            .join(' · ');
                        return (
                            <li
                                key={sale.saleId}
                                className="flex items-start gap-3 py-2 text-[11.5px]"
                            >
                                <div className="min-w-0 flex-1">
                                    <p className="truncate font-medium">{products}</p>
                                    <p className="text-[10.5px] text-muted-foreground">
                                        {formatDateTimeMx(sale.soldAt)} ·{' '}
                                        {formatMoneyCents(totalCents)} ·{' '}
                                        {directory.storeName(sale.storeId)}
                                        {rules.length > 0 ? ` · reglas: ${rules.join(', ')}` : ''}
                                    </p>
                                </div>
                                {sale.status === CommissionAccrualStatusEnum.REVERSED ? (
                                    <StatusPill tone="danger">Revertida</StatusPill>
                                ) : (
                                    <span className="font-semibold tabular-nums text-success">
                                        +{formatMoneyCents(sale.subtotalBonusCents)}
                                    </span>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
            <div className="mt-3 flex justify-end">
                <Button onClick={onClose}>Entendido</Button>
            </div>
        </Modal>
    );
}
